/**
 * Reachable paymentType values for open orders from order.list.getLastOpenOrder.
 *
 * Excludes apple_pay and gui_pp_express (filtered at DB level).
 * Payment-service methods (Adyen/Twint) cause early empty return from PHP.
 */
export const OPEN_ORDER_PAYMENT_TYPE = {
  GUI_VISA: 'gui_visa',
  GUI_MASTER: 'gui_master',
  GUI_DANKORT: 'gui_dankort',
  GUI_MAESTRO: 'gui_maestro',
  GUI_AMEX: 'gui_amex',
  PAYPAL: 'paypal',
  IDEAL: 'ideal'
} as const;

/**
 * Computop payment gateway request URLs per payment method group.
 *
 * Credit cards (gui_visa, gui_master, gui_amex, gui_dankort, gui_maestro) → payssl.aspx
 * PayPal → paypal.aspx
 * iDEAL → payOTF.aspx
 */
export const COMPUTOP_REQUEST_URL = {
  CREDIT_CARD: 'https://www.computop-paygate.com/payssl.aspx',
  PAYPAL: 'https://www.computop-paygate.com/paypal.aspx',
  IDEAL: 'https://www.computop-paygate.com/payOTF.aspx'
} as const;

/**
 * Human-readable display labels for open order payment types.
 *
 * Used wherever the UI needs to show a payment method name to the user,
 * e.g. in the cart overview when an unpaid order is awaiting Computop
 * GUI payment. Keyed by the PSI `paymentType` string so lookups are direct:
 *
 * @example
 * OPEN_ORDER_PAYMENT_TYPE_LABEL[openOrder.paymentType] // → "Mastercard"
 */
export const OPEN_ORDER_PAYMENT_TYPE_LABEL = {
  gui_visa: 'Visa',
  gui_master: 'Mastercard',
  gui_dankort: 'Dankort',
  gui_maestro: 'Maestro',
  gui_amex: 'American Express',
  paypal: 'PayPal',
  ideal: 'iDEAL'
} satisfies Record<(typeof OPEN_ORDER_PAYMENT_TYPE)[keyof typeof OPEN_ORDER_PAYMENT_TYPE], string>;

/** PSI category values used for shipping addresses. */
export const ADDRESS_SHIPPING_CATEGORY = {
  PRIVATE: 1,
  COMPANY: 2,
  PICKUP_POINT: 3,
  PERSONAL: 4
} as const;

export type AddressShippingCategory = (typeof ADDRESS_SHIPPING_CATEGORY)[keyof typeof ADDRESS_SHIPPING_CATEGORY];

/** PSI category values used for distinct billing addresses. */
export const ADDRESS_BILLING_CATEGORY = {
  PERSONAL: 4,
  COMPANY: 5
} as const;

/** PSI names of the SEPA fields in `shoppingcart.payment.set` and in its field errors. */
export const PSI_SEPA_FIELD = {
  HOLDER: 'payeeName',
  IBAN: 'paymentNumber',
  BIC: 'creditInstituteNumber',
  BANK_NAME: 'creditInstituteName',
  ACCEPTED: 'paymentConditionAccept'
} as const;
